/**
 * @file Resolves one `tableTypes/<tableId>` document into flat column field
 * options for a mapping-review table slice: plain field columns and entity-
 * reference columns (carrying `reference` metadata used to detect reference
 * cells). Shared by the per-table column cards (`MappingReviewColumns`) and
 * the per-table row grid (`MappingReviewRows`) so both classify reference
 * targets identically. Owns its own document subscription per consumer; a
 * retargeted table re-points the subscription at the new `tableId`.
 */
import { useEffect, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { UIElementType } from '#types';
import type { ArTableType } from '#types';
import { createDocumentStore } from '@/store';
import type { TargetFieldOption } from '@/services/editorExtractors';

function tableTargetFields(table: ArTableType): TargetFieldOption[] {
  const fields: TargetFieldOption[] = [];
  const seen = new Set<string>();
  for (const col of table.columns) {
    const id = String(col.typeId);
    if (seen.has(id)) continue;
    const label = col.descriptionOverride?.shortLabel ?? col.cacheDescription ?? id;
    if (col.uiElementType === UIElementType.Field) {
      seen.add(id);
      fields.push({ id, label });
    } else if (col.referenceType === 'entity' && col.entityId) {
      seen.add(id);
      fields.push({
        id,
        label,
        reference: {
          entityId: col.entityId,
          ...(col.entityGroupId ? { entityGroupId: col.entityGroupId } : {}),
        },
      });
    }
  }
  return fields;
}

export function useTableTypeFields(tableId: string): { loading: boolean; fields: TargetFieldOption[] } {
  const path = `tableTypes/${tableId}`;
  const store = useMemo(() => createDocumentStore<ArTableType>(path), [path]);
  useEffect(() => {
    void store.connect();
    return () => store.disconnect();
  }, [store]);
  const snap = useSnapshot(store.state);
  return useMemo(() => {
    const data = snap.data;
    if (!data) return { loading: snap.loading, fields: [] };
    return { loading: false, fields: tableTargetFields(data as ArTableType) };
  }, [snap.data, snap.loading]);
}
